import { i18n } from '@/shared/config/i18n/i18n';

import type { ApiError, ApiErrorEnvelope } from './types';

// The one place that reads failed requests. Every 4xx comes as `{ error: { code, message, fields } }`;
// anything else (no connection, an HTML error page, a 5xx) becomes a generic error with a `client_` code.

const isApiError = (value: unknown): value is ApiError =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as ApiError).code === 'string' &&
  typeof (value as ApiError).message === 'string';

const isEnvelope = (value: unknown): value is ApiErrorEnvelope =>
  !!value &&
  typeof value === 'object' &&
  isApiError((value as ApiErrorEnvelope).error);

const clientError = (code: string): ApiError => ({
  code: `client_${code}`,
  message: i18n.t('Server response error'),
  fields: null,
});

const parse = (requestError: object): ApiError => {
  if (isApiError(requestError)) return requestError;

  const error = requestError as { status?: unknown; data?: unknown };

  if (isEnvelope(error.data)) return error.data.error;

  // `{ error: FetchBaseQueryError }`, as some callers pass the result of a request
  if (error.status === undefined && 'error' in requestError) {
    const inner = (requestError as { error: unknown }).error;
    if (inner && typeof inner === 'object') return parse(inner);
  }

  switch (error.status) {
    case 'FETCH_ERROR':
      return clientError('fetch_error');
    case 'TIMEOUT_ERROR':
      return clientError('timeout');
    case 'PARSING_ERROR':
      return clientError('parsing_error');
    default:
      return clientError('unknown');
  }
};

// Components parse errors while rendering and react to them in `useEffect`, so one
// failed request must give the same object every time; a new failure gives a new one
const parsed = new WeakMap<object, ApiError>();

// Takes what RTK Query gives on failure: the hook's `error`, what `unwrap()` throws,
// or either of them wrapped in `{ error }`. An already parsed error is returned as is.
export const parseApiError = (requestError: unknown): ApiError | null => {
  if (requestError == null) return null;
  if (typeof requestError !== 'object') return clientError('unknown');

  let error = parsed.get(requestError);
  if (!error) {
    error = parse(requestError);
    parsed.set(requestError, error);
  }

  return error;
};

// The text to show for a failed request: its message, or `fallback` (translated) when there is none
export const getApiErrorMessage = (
  requestError: unknown,
  fallback = 'Server response error',
): string => {
  const error = parseApiError(requestError);

  if (!error || error.code.startsWith('client_')) return i18n.t(fallback);

  const fieldMessage = Object.values(error.fields ?? {}).flat()[0];

  return error.message || fieldMessage || i18n.t(fallback);
};

// Whether the backend rejected the given form field
export const hasFieldError = (requestError: unknown, field: string) =>
  !!parseApiError(requestError)?.fields?.[field];
